import { useState, type ReactNode } from 'react'

interface TooltipProps {
  /** The content shown on hover — can be rich JSX (multi-line, colored thresholds). */
  content: ReactNode
  /** The trigger element. If omitted, a small ⓘ info icon is rendered. */
  children?: ReactNode
  /** Max width of the tooltip bubble in px. */
  width?: number
}

/**
 * Lightweight hover tooltip. Pure CSS/React (no portal, no deps).
 * Renders above the trigger, centered, with a small arrow. Used for column-header
 * explanations on the Watchlist. For a plain trigger, pass no children and an ⓘ icon
 * is shown.
 */
export function Tooltip({ content, children, width = 260 }: TooltipProps) {
  const [open, setOpen] = useState(false)

  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      {children ?? (
        <span
          aria-label="More info"
          className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-current text-[9px] leading-none text-[var(--color-text-muted)] cursor-help select-none"
        >
          i
        </span>
      )}
      {open && (
        <span
          role="tooltip"
          style={{ width, whiteSpace: 'normal' }}
          className="absolute top-full left-1/2 z-[100] mt-2 -translate-x-1/2 block rounded-lg bg-slate-800 px-3 py-2 text-left text-xs font-normal normal-case leading-snug text-white shadow-xl ring-1 ring-black/20 break-words"
        >
          {/* arrow (points up toward the trigger) */}
          <span className="absolute left-1/2 bottom-full h-0 w-0 -translate-x-1/2 border-x-4 border-b-4 border-x-transparent border-b-slate-800" />
          {content}
        </span>
      )}
    </span>
  )
}

/**
 * Preset tooltip contents for the 3 valuation metrics (from the user's reference table).
 * Green = "good/cheap", Red = "bad/expensive".
 */
export const VALUATION_TOOLTIPS: Record<'forward_pe' | 'peg_ratio' | 'price_sales', ReactNode> = {
  forward_pe: (
    <span className="block space-y-1">
      <span className="block font-semibold">Forward P/E</span>
      <span className="block text-white/80">The price you pay for next year's expected profits.</span>
      <span className="block"><span className="text-green-400">●</span> Good: below 20 (fair for tech/hardware)</span>
      <span className="block"><span className="text-red-400">●</span> Expensive: above 30–35 (future profits over-hyped)</span>
    </span>
  ),
  peg_ratio: (
    <span className="block space-y-1">
      <span className="block font-semibold">PEG Ratio</span>
      <span className="block text-white/80">Price vs. how fast profits are growing.</span>
      <span className="block"><span className="text-green-400">●</span> Good: below 1.0 (a growth discount)</span>
      <span className="block"><span className="text-red-400">●</span> Expensive: above 2.0 (growth can't justify price)</span>
    </span>
  ),
  price_sales: (
    <span className="block space-y-1">
      <span className="block font-semibold">Price / Sales (P/S)</span>
      <span className="block text-white/80">Price paid per dollar of real revenue. Useful when margins swing.</span>
      <span className="block"><span className="text-green-400">●</span> Good: under 12–13 (vs. sector)</span>
      <span className="block"><span className="text-red-400">●</span> Expensive: high double-digits (overvalued)</span>
    </span>
  ),
}
